import React, { useCallback, useEffect, useRef, useState } from 'react'
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Divider,
    FormControlLabel,
    Grid,
    IconButton,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Paper,
    Switch,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material'
import { DeleteOutline, ExpandMore, ExpandLess, WarningAmber, AttachFile, AddCircleOutline, Build } from '@mui/icons-material'
import {
    DragDropContext,
    Droppable,
    Draggable
} from "@hello-pangea/dnd";
import OperationCard from './OperationCard';
import RoutingDAGPreview from './RoutingDAGPreview';
import {
    uploadOperationAttachment,
    getOperationAttachments,
    deleteOperationAttachment,
    downloadOperationAttachment,
} from '../../services/bomService';

// Parallel path colour mapping
const PATH_COLOURS_LIST = [
    '#1677ff', '#52c41a', '#fa8c16', '#722ed1', '#eb2f96',
    '#13c2c2', '#faad14', '#a0d911',
];
const getPathBgColour = (path, allPaths) => {
    if (!path) return '#e5e7eb';
    const idx = allPaths.indexOf(path);
    return PATH_COLOURS_LIST[Math.max(idx, 0) % PATH_COLOURS_LIST.length];
};

const COST_TYPES = ['CALCULATED', 'FIXED_RATE', 'SUB_CONTRACTED', 'RATE_TIMES_QTY'];
const BORDER_COLOR = '#e5e7eb';
const HEADER_BG = '#0f2744';

const fieldSx = {
    "& .MuiInputBase-input": { fontSize: 13 },
    "& .MuiInputLabel-root": { fontSize: 13 },
    "& .MuiOutlinedInput-root": {
        borderRadius: 1,
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#90caf9' },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#1565c0' },
    },
};

const SectionLabel = ({ children }) => (
    <Typography variant="caption" fontWeight={600} color="#0f2744"
        sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8, mt: 2, mb: 0.5, display: 'block' }}>
        {children}
    </Typography>
);

export default function BomRouting({
    operations,
    setOperations,
    fetchProductionJob,
    jobs,
    error,
    loading,
    fetchWorkCenter,
    workCenters,
    fetchLaborRoles,
    laborRoles,
    fetchMachines,
    machines,
    selectedOperationKey: selectedKeyProp,
    onSelectOperationKey,
}) {

    const [searchQuery, setSearchQuery] = useState('');
    const [workCenterSearchQuery, setWorkCenterSearchQuery] = useState('');
    const [laborRoleSearchQuery, setLaborRoleSearchQuery] = useState('');
    // Which operation is open in the editor. The page keeps it when it passes the key in, so the
    // same operation is still open after switching tabs and back.
    const [localSelectedKey, setLocalSelectedKey] = useState(null);
    const selectedKey = onSelectOperationKey ? selectedKeyProp ?? null : localSelectedKey;
    const setSelectedKey = onSelectOperationKey || setLocalSelectedKey;

    // The editor works on the operation inside `operations` itself, not on a copy: every edit lands
    // in the BOM's own list at once. A copy only reached the list on "Save Changes", so picking
    // another operation or leaving the tab silently threw the edits away.
    const opKey = (op) => op?.id ?? op?._tempId;
    const selectedOperation = selectedKey == null ? null
        : (operations || []).find(op => opKey(op) === selectedKey) ?? null;
    const setSelectedOperation = (next) => {
        if (!next) { setSelectedKey(null); return; }
        const key = opKey(next);
        setSelectedKey(key);
        setOperations(ops => (ops || []).map(op => opKey(op) === key ? next : op));
    };
    const [depsExpanded, setDepsExpanded] = useState(false);
    const [selfDepAlert, setSelfDepAlert] = useState(false);

    // Attachment state
    const [opAttachments, setOpAttachments] = useState([]);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [attachmentUploading, setAttachmentUploading] = useState(false);
    const [attachmentsExpanded, setAttachmentsExpanded] = useState(true);

    // Compute unique parallel paths across all operations for colour legend
    const allParallelPaths = [...new Set((operations || []).map(o => o.parallelPath).filter(Boolean))];

    // Build set of operation IDs that are referenced as a dependency by other ops
    const depsTargetIds = new Set(
        (operations || []).flatMap(op =>
            (op.dependencies || []).map(d => d.dependsOnRoutingOperationId)
        )
    );

    // Returns true if op has a parallelPath set but allowParallel is false
    const hasPathMismatch = (op) => Boolean(op.parallelPath && !op.allowParallel);

    // Returns true if op is in a dependency chain (either has deps or is a dep target) AND allowParallel=false
    const hasParallelChainWarning = (op) => {
        const id = op.id || op._tempId;
        const hasDeps = (op.dependencies || []).length > 0;
        const isTarget = depsTargetIds.has(id);
        return (hasDeps || isTarget) && !op.allowParallel && Boolean(op.parallelPath);
    };

    const handleAddOperation = () => {
        const nextSeq = ((operations?.length || 0) + 1) * 10;
        const newOp = {
            _tempId: Date.now(),
            name: "New Operation",
            sequenceNumber: nextSeq,
            setupTime: "",
            runTime: "",
            inspection: false,
            notes: "",
            numberOfOperators: 1,
            costType: "CALCULATED",
            fixedCostPerUnit: null,
            costQuantity: null,
            costRate: null,
        };
        setOperations([...operations || [], newOp]);
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result.map((op, index) => ({ ...op, sequenceNumber: (index + 1) * 10 }));
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        setOperations(reorder(operations, result.source.index, result.destination.index));
    };

    useEffect(() => { fetchProductionJob(searchQuery); }, [searchQuery]);
    useEffect(() => { fetchWorkCenter(workCenterSearchQuery); }, [workCenterSearchQuery]);
    useEffect(() => { if (fetchLaborRoles) fetchLaborRoles(laborRoleSearchQuery); }, [laborRoleSearchQuery]);
    useEffect(() => { if (fetchMachines) fetchMachines(); }, []);

    const handleDeleteOperation = () => {
        if (!selectedOperation) return;
        setOperations(operations.filter(
            op => (op.id || op._tempId) !== (selectedOperation.id || selectedOperation._tempId)
        ));
        setSelectedOperation(null);
    };

    // After the BOM is saved the list is reloaded from the server: a new operation's temporary key
    // is gone and it has a real id. Find it again by sequence and name so it stays open.
    const lastSelectedRef = useRef(null);
    if (selectedOperation) lastSelectedRef.current = selectedOperation;
    useEffect(() => {
        if (selectedKey == null || selectedOperation) return;
        const last = lastSelectedRef.current;
        const match = last && (operations || []).find(op =>
            op.sequenceNumber === last.sequenceNumber && op.name === last.name);
        setSelectedKey(match ? opKey(match) : null);
    }, [operations]);

    // Fetch attachments when an existing (saved) operation is selected
    const fetchOpAttachments = useCallback(async (opId) => {
        if (!opId) { setOpAttachments([]); return; }
        try {
            const data = await getOperationAttachments(opId);
            setOpAttachments(data || []);
        } catch { setOpAttachments([]); }
    }, []);

    useEffect(() => {
        if (selectedOperation?.id) {
            fetchOpAttachments(selectedOperation.id);
        } else {
            setOpAttachments([]);
        }
        setAttachmentFile(null);
    }, [selectedOperation?.id, fetchOpAttachments]);

    const handleAttachmentUpload = async () => {
        if (!attachmentFile || !selectedOperation?.id) return;
        setAttachmentUploading(true);
        try {
            await uploadOperationAttachment(selectedOperation.id, attachmentFile);
            setAttachmentFile(null);
            await fetchOpAttachments(selectedOperation.id);
        } catch (e) {
            console.error('Failed to upload attachment', e);
        }
        setAttachmentUploading(false);
    };

    const handleAttachmentDelete = async (fileId) => {
        if (!selectedOperation?.id) return;
        try {
            await deleteOperationAttachment(selectedOperation.id, fileId);
            await fetchOpAttachments(selectedOperation.id);
        } catch (e) {
            console.error('Failed to delete attachment', e);
        }
    };

    const handleAttachmentDownload = async (fileId, fileName) => {
        try {
            await downloadOperationAttachment(fileId, fileName);
        } catch (e) {
            console.error('Failed to download attachment', e);
        }
    };

    const isCalculated = selectedOperation?.costType === 'CALCULATED' || !selectedOperation?.costType;
    const isRateTimesQty = selectedOperation?.costType === 'RATE_TIMES_QTY';
    const isFlatCost = selectedOperation?.costType === 'FIXED_RATE' || selectedOperation?.costType === 'SUB_CONTRACTED';

    // Effective per-each rate for a piece-rate op: operation override, else the job's standard rate.
    const jobPieceRate = selectedOperation?.productionJob?.defaultPieceRate;
    const jobPieceUnit = selectedOperation?.productionJob?.pieceUnit;
    const effectivePieceRate = (selectedOperation?.costRate != null && selectedOperation?.costRate !== '')
        ? Number(selectedOperation.costRate)
        : (jobPieceRate != null ? Number(jobPieceRate) : null);
    const pieceLineCost = (effectivePieceRate != null && selectedOperation?.costQuantity)
        ? effectivePieceRate * Number(selectedOperation.costQuantity)
        : null;

    return (
        <Grid container sx={{ height: "80vh", overflow: "hidden" }} spacing={0}>

            {/* LEFT PANEL — Operations List */}
            <Grid item xs={2.5}
                sx={{ 
                    borderRight: `1px solid ${BORDER_COLOR}`, 
                    height: "100%", 
                    bgcolor: '#fafbfc',
                    boxShadow: 'inset -2px 0 8px rgba(0,0,0,0.02)'
                }}>
                <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
                    <Box sx={{
                        bgcolor: HEADER_BG, color: '#fff', px: 2, py: 1.25, borderRadius: 2, mb: 2,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                            Operations
                        </Typography>
                        <Chip 
                            label={operations?.length || 0} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, height: 20, fontSize: '0.7rem' }} 
                        />
                    </Box>

                    <Button
                        variant="contained"
                        onClick={handleAddOperation}
                        fullWidth
                        size="small"
                        startIcon={<AddCircleOutline sx={{ fontSize: 18 }} />}
                        sx={{
                            mb: 2, textTransform: 'none', fontWeight: 600, fontSize: '0.8rem',
                            bgcolor: '#1565c0', color: '#fff', borderRadius: 1.5, py: 1,
                            boxShadow: '0 2px 6px rgba(21,101,192,0.2)',
                            '&:hover': { bgcolor: '#0d47a1' },
                        }}
                    >
                        New Operation
                    </Button>

                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="operationsDroppable">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {operations?.map((operation, index) => (
                                        <Draggable
                                            key={(operation.id || operation._tempId).toString()}
                                            draggableId={(operation.id || operation._tempId).toString()}
                                            index={index}
                                        >
                                            {(provided, snapshot) => (
                                                <Paper
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                    elevation={snapshot.isDragging ? 4 : 0}
                                                    sx={{
                                                        p: 1.5, mb: 1, cursor: "pointer", borderRadius: 2,
                                                        border: `1px solid ${BORDER_COLOR}`,
                                                        borderLeft: selectedOperation &&
                                                            (selectedOperation.id || selectedOperation._tempId) ===
                                                            (operation.id || operation._tempId)
                                                            ? "4px solid #1565c0"
                                                            : hasParallelChainWarning(operation)
                                                                ? "4px solid #fa8c16"
                                                                : `1px solid ${BORDER_COLOR}`,
                                                        bgcolor: selectedOperation &&
                                                            (selectedOperation.id || selectedOperation._tempId) ===
                                                            (operation.id || operation._tempId)
                                                            ? '#f0f7ff' : '#fff',
                                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                        boxShadow: selectedOperation &&
                                                            (selectedOperation.id || selectedOperation._tempId) ===
                                                            (operation.id || operation._tempId)
                                                            ? '0 2px 8px rgba(21,101,192,0.12)' : 'none',
                                                        "&:hover": { 
                                                            bgcolor: selectedOperation && (selectedOperation.id || selectedOperation._tempId) === (operation.id || operation._tempId) ? '#e3f2fd' : '#f9fafb',
                                                            transform: 'translateY(-1px)',
                                                            boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                                                        },
                                                    }}
                                                    onClick={() => {
                                                        setSelectedKey(opKey(operation));
                                                        setSelfDepAlert(false);
                                                        setDepsExpanded(false);
                                                    }}
                                                >
                                                    <Box display="flex" alignItems="center" justifyContent="space-between" gap={0.5}>
                                                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: '#0f2744' }}>
                                                            {operation.sequenceNumber}. {operation.name}
                                                        </Typography>
                                                        {hasParallelChainWarning(operation) && (
                                                            <Tooltip title="parallelPath set but allowParallel is off" arrow>
                                                                <WarningAmber sx={{ fontSize: 16, color: '#fa8c16', flexShrink: 0 }} />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                    {operation.productionJob && (
                                                        <Typography sx={{ fontSize: 11.5, color: '#6b7280', mt: 0.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Build sx={{ fontSize: 12 }} /> {operation.productionJob.jobName}
                                                        </Typography>
                                                    )}
                                                </Paper>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </Box>
            </Grid>

            {/* CENTER PANEL — DAG Preview */}
            <Grid item xs={5.5}
                sx={{ borderRight: `1px solid ${BORDER_COLOR}`, height: "100%" }}
                bgcolor={"#f8f9fa"}>
                <Box sx={{ p: 2, height: "100%", overflowY: "auto" }}>
                    <Box sx={{
                        bgcolor: HEADER_BG, color: '#fff', px: 2, py: 1, borderRadius: 2, mb: 2,
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>Visual Flow</Typography>
                        <Typography sx={{ fontSize: '0.7rem', opacity: 0.7, fontWeight: 500 }}>Live Dependency Graph</Typography>
                    </Box>
                    {(!operations || operations.length === 0) ? (
                        <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="70%" gap={2}>
                            <Box sx={{ p: 3, borderRadius: '50%', bgcolor: '#fff', border: `1px dashed ${BORDER_COLOR}` }}>
                                <Build sx={{ fontSize: 40, color: '#d1d5db' }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', fontWeight: 500 }}>
                                No operations defined.<br />Get started by adding one from the left panel.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ borderRadius: 2, overflow: 'hidden', border: `1px solid ${BORDER_COLOR}`, bgcolor: '#fff', height: 'calc(100% - 60px)' }}>
                            <RoutingDAGPreview operations={operations} />
                        </Box>
                    )}
                </Box>
            </Grid>

            {/* RIGHT PANEL — Operation Details */}
            <Grid item xs={4} sx={{ height: "100%" }}>
                <Box sx={{ p: 2, height: "100%", overflowY: "auto", bgcolor: '#fff' }}>

                    <Box sx={{
                        bgcolor: HEADER_BG, color: '#fff', px: 2, py: 1.25, borderRadius: 2, mb: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
                            {selectedOperation ? `Edit: ${selectedOperation.name}` : 'Operation Details'}
                        </Typography>
                    </Box>

                    {!selectedOperation && (
                        <Typography sx={{ color: "#9ca3af", mt: 3, textAlign: 'center', fontSize: '0.85rem' }}>
                            Select an operation to edit its details
                        </Typography>
                    )}

                    {selectedOperation && (
                        <>
                            {/* Basic Info */}
                            <SectionLabel>Basic Info</SectionLabel>
                            <TextField fullWidth size="small" label="Operation Name" sx={{ ...fieldSx, mb: 1.5 }}
                                value={selectedOperation.name || ""}
                                onChange={(e) => setSelectedOperation({ ...selectedOperation, name: e.target.value })}
                            />

                            {/* Resource Assignment */}
                            <SectionLabel>Resource Assignment</SectionLabel>
                            <Autocomplete fullWidth size="small" options={jobs}
                                value={selectedOperation.productionJob || null}
                                getOptionLabel={(o) => o?.jobName ? `${o.jobName} (${o.jobCode || ''})` : ""}
                                onChange={(e, v) => setSelectedOperation({
                                    ...selectedOperation,
                                    productionJob: v,
                                    // A new operation takes the job's work centre; a saved one keeps the one it has.
                                    ...(!selectedOperation.id ? { workCenter: v?.workCenter ?? null } : {}),
                                })}
                                renderInput={(params) => <TextField {...params} size="small" label="Production Job" sx={fieldSx} />}
                                onInputChange={(e, v) => setSearchQuery(v)} loading={loading}
                                sx={{ mb: 1.5 }}
                            />

                            <Autocomplete fullWidth size="small" options={workCenters}
                                value={selectedOperation.workCenter || null}
                                getOptionLabel={(o) => o?.centerCode ? `${o.centerName} (${o.centerCode})` : ""}
                                onChange={(e, v) => setSelectedOperation({ ...selectedOperation, workCenter: v })}
                                renderInput={(params) => <TextField {...params} size="small" label="Work Center" sx={fieldSx} />}
                                onInputChange={(e, v) => setWorkCenterSearchQuery(v)} loading={loading}
                                sx={{ mb: 1.5 }}
                            />

                            <Autocomplete fullWidth size="small" options={laborRoles || []}
                                value={selectedOperation.laborRole || null}
                                getOptionLabel={(o) => o?.roleName ? `${o.roleName} (${o.roleCode || ''})` : ""}
                                onChange={(e, v) => setSelectedOperation({ ...selectedOperation, laborRole: v })}
                                renderInput={(params) => <TextField {...params} size="small" label="Labor Role" sx={fieldSx} />}
                                onInputChange={(e, v) => setLaborRoleSearchQuery(v)} loading={loading}
                                sx={{ mb: 1.5 }}
                            />

                            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                <Grid item xs={6}>
                                    <TextField fullWidth size="small" type="number" label="Operators" sx={fieldSx}
                                        value={selectedOperation.numberOfOperators ?? 1}
                                        onChange={(e) => setSelectedOperation({ ...selectedOperation, numberOfOperators: parseInt(e.target.value) || 1 })}
                                        inputProps={{ min: 1 }}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <Autocomplete fullWidth size="small" options={machines || []}
                                        value={selectedOperation.machineDetails || null}
                                        getOptionLabel={(o) => o?.machineName ? `${o.machineName} (${o.machineCode || ''})` : ""}
                                        onChange={(e, v) => setSelectedOperation({ ...selectedOperation, machineDetails: v })}
                                        renderInput={(params) => <TextField {...params} size="small" label="Machine" sx={fieldSx} />}
                                        loading={loading}
                                    />
                                </Grid>
                            </Grid>

                            {/* Timing & Costing */}
                            <SectionLabel>Timing & Costing</SectionLabel>
                            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                <Grid item xs={6}>
                                    <TextField fullWidth size="small" type="number" label="Setup Time (min)" sx={fieldSx}
                                        value={selectedOperation.setupTime || ""}
                                        onChange={(e) => setSelectedOperation({ ...selectedOperation, setupTime: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <TextField fullWidth size="small" type="number" label="Run Time (min/unit)" sx={fieldSx}
                                        value={selectedOperation.runTime || ""}
                                        onChange={(e) => setSelectedOperation({ ...selectedOperation, runTime: e.target.value })}
                                    />
                                </Grid>
                            </Grid>

                            {/* Scheduling: time between operations */}
                            <SectionLabel>Between Operations</SectionLabel>
                            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                                <Grid item xs={4}>
                                    <Tooltip title="Minimum wait after material arrives before this operation can start (staging, inspection, cooling). Clock minutes." arrow>
                                        <TextField fullWidth size="small" type="number" label="Queue (min)" sx={fieldSx}
                                            inputProps={{ min: 0 }}
                                            value={selectedOperation.queueTime ?? ""}
                                            onChange={(e) => setSelectedOperation({ ...selectedOperation, queueTime: e.target.value === "" ? null : e.target.value })}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={4}>
                                    <Tooltip title="Time to carry this operation's output to the next one. Clock minutes." arrow>
                                        <TextField fullWidth size="small" type="number" label="Move (min)" sx={fieldSx}
                                            inputProps={{ min: 0 }}
                                            value={selectedOperation.moveTime ?? ""}
                                            onChange={(e) => setSelectedOperation({ ...selectedOperation, moveTime: e.target.value === "" ? null : e.target.value })}
                                        />
                                    </Tooltip>
                                </Grid>
                                <Grid item xs={4}>
                                    <Tooltip title="Pass output on in batches of this size so the next operation can start before this one finishes. Leave blank to move the whole lot at once." arrow>
                                        <TextField fullWidth size="small" type="number" label="Transfer batch" sx={fieldSx}
                                            inputProps={{ min: 0 }}
                                            value={selectedOperation.transferQuantity ?? ""}
                                            onChange={(e) => setSelectedOperation({ ...selectedOperation, transferQuantity: e.target.value === "" ? null : e.target.value })}
                                        />
                                    </Tooltip>
                                </Grid>
                            </Grid>

                            <TextField select fullWidth size="small" label="Cost Type" sx={{ ...fieldSx, mb: 1.5 }}
                                value={selectedOperation.costType || "CALCULATED"}
                                onChange={(e) => {
                                    const ct = e.target.value;
                                    setSelectedOperation({
                                        ...selectedOperation,
                                        costType: ct,
                                        // Clear cost fields that don't apply to the newly-selected type
                                        fixedCostPerUnit: (ct === 'FIXED_RATE' || ct === 'SUB_CONTRACTED') ? selectedOperation.fixedCostPerUnit : null,
                                        costQuantity: ct === 'RATE_TIMES_QTY' ? selectedOperation.costQuantity : null,
                                        costRate: ct === 'RATE_TIMES_QTY' ? selectedOperation.costRate : null,
                                        applyOverhead: ct === 'RATE_TIMES_QTY' ? Boolean(selectedOperation.applyOverhead) : false,
                                    });
                                }}
                            >
                                {COST_TYPES.map((ct) => <MenuItem key={ct} value={ct}>{ct.replace(/_/g, ' ')}</MenuItem>)}
                            </TextField>

                            {isFlatCost && (
                                <TextField fullWidth size="small" type="number" label="Fixed Cost Per Unit (₹)" sx={{ ...fieldSx, mb: 1.5 }}
                                    value={selectedOperation.fixedCostPerUnit ?? ""}
                                    onChange={(e) => setSelectedOperation({
                                        ...selectedOperation, fixedCostPerUnit: e.target.value !== '' ? parseFloat(e.target.value) : null
                                    })}
                                />
                            )}

                            {isRateTimesQty && (
                                <>
                                    <Grid container spacing={1.5} sx={{ mb: 1 }}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" type="number"
                                                label={`Quantity${jobPieceUnit ? ` (${jobPieceUnit})` : ''}`}
                                                sx={fieldSx}
                                                value={selectedOperation.costQuantity ?? ""}
                                                onChange={(e) => setSelectedOperation({
                                                    ...selectedOperation, costQuantity: e.target.value !== '' ? parseFloat(e.target.value) : null
                                                })}
                                                placeholder="e.g. 12"
                                            />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" type="number"
                                                label="Rate Override (₹/each)"
                                                sx={fieldSx}
                                                value={selectedOperation.costRate ?? ""}
                                                onChange={(e) => setSelectedOperation({
                                                    ...selectedOperation, costRate: e.target.value !== '' ? parseFloat(e.target.value) : null
                                                })}
                                                placeholder={jobPieceRate != null ? `Job: ${jobPieceRate}` : 'Set on Job'}
                                            />
                                        </Grid>
                                    </Grid>
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5, fontSize: '0.72rem' }}>
                                        {effectivePieceRate == null ? (
                                            <>No rate — set <strong>Default Piece Rate</strong> on the Production Job, or a rate override here.</>
                                        ) : (
                                            <>
                                                Rate {selectedOperation.costRate != null && selectedOperation.costRate !== '' ? '(override)' : '(from Job)'}:{' '}
                                                <strong>₹{effectivePieceRate}</strong>/{jobPieceUnit || 'each'}
                                                {pieceLineCost != null && <> × {selectedOperation.costQuantity} = <strong>₹{pieceLineCost.toFixed(2)}</strong></>}
                                            </>
                                        )}
                                    </Typography>
                                    <Tooltip arrow title="Used only when the BOM's Overhead % is blank. A piece rate is treated as all-in unless this is ticked.">
                                        <FormControlLabel sx={{ mt: -0.5, mb: 1 }}
                                            control={
                                                <Checkbox size="small" checked={Boolean(selectedOperation.applyOverhead)}
                                                    onChange={(e) => setSelectedOperation({ ...selectedOperation, applyOverhead: e.target.checked })}
                                                    sx={{ '&.Mui-checked': { color: '#1565c0' } }}
                                                />
                                            }
                                            label={<Typography sx={{ fontSize: '0.8rem' }}>Add work center overhead to this piece rate</Typography>}
                                        />
                                    </Tooltip>
                                </>
                            )}

                            {/* Options */}
                            <FormControlLabel sx={{ mt: 0.5, mb: 0.5 }}
                                control={
                                    <Checkbox size="small" checked={selectedOperation.inspection || false}
                                        onChange={(e) => setSelectedOperation({ ...selectedOperation, inspection: e.target.checked })}
                                        sx={{ '&.Mui-checked': { color: '#1565c0' } }}
                                    />
                                }
                                label={<Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Requires Inspection</Typography>}
                            />

                            {/* Manufacturing Instructions */}
                            <SectionLabel>Manufacturing Instructions</SectionLabel>
                            <TextField 
                                fullWidth 
                                size="small" 
                                label="SOP / Work Instructions" 
                                sx={{ ...fieldSx, mb: 1.5 }}
                                value={selectedOperation.instructions || ""}
                                onChange={(e) => setSelectedOperation({ ...selectedOperation, instructions: e.target.value })}
                                multiline 
                                minRows={4}
                                placeholder="Enter detailed steps for the operator..."
                            />

                            {/* ── Parallel Operation Controls ── */}
                            <SectionLabel>Parallel Execution</SectionLabel>
                            <FormControlLabel
                                sx={{ mb: 0.75 }}
                                control={
                                    <Switch
                                        size="small"
                                        checked={Boolean(selectedOperation.allowParallel)}
                                        onChange={(e) => setSelectedOperation({
                                            ...selectedOperation,
                                            allowParallel: e.target.checked,
                                            parallelPath: e.target.checked ? (selectedOperation.parallelPath || '') : '',
                                        })}
                                        sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#1565c0' } }}
                                    />
                                }
                                label={
                                    <Box display="flex" alignItems="center" gap={0.75}>
                                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>Allow Parallel</Typography>
                                        {selectedOperation.allowParallel && (
                                            <Chip size="small" label="Parallel" sx={{ bgcolor: '#e6f4ff', color: '#1677ff', fontWeight: 700, fontSize: '0.68rem', height: 18 }} />
                                        )}
                                    </Box>
                                }
                            />

                            {selectedOperation.allowParallel && (
                                <TextField
                                    fullWidth
                                    size="small"
                                    label="Parallel Path (e.g. PATH_A)"
                                    sx={{ ...fieldSx, mb: 1.5 }}
                                    value={selectedOperation.parallelPath || ''}
                                    placeholder="PATH_A, PATH_B…"
                                    onChange={(e) => setSelectedOperation({ ...selectedOperation, parallelPath: e.target.value.toUpperCase() })}
                                    InputProps={selectedOperation.parallelPath ? {
                                        endAdornment: (
                                            <Chip
                                                size="small"
                                                label={selectedOperation.parallelPath}
                                                sx={{
                                                    bgcolor: getPathBgColour(selectedOperation.parallelPath, allParallelPaths),
                                                    color: '#fff',
                                                    fontWeight: 700,
                                                    fontSize: '0.68rem',
                                                    height: 20,
                                                    mr: -0.5,
                                                }}
                                            />
                                        ),
                                    } : {}}
                                />
                            )}
                            {/* Path-mismatch warning: parallelPath set but allowParallel is off */}
                            {hasPathMismatch(selectedOperation) && (
                                <Alert severity="warning" sx={{ mb: 1, py: 0.25, fontSize: '0.74rem' }}>
                                    <strong>parallelPath</strong> is set but <strong>Allow Parallel</strong> is off — path will be ignored by the scheduler.
                                </Alert>
                            )}

                            {/* Dependencies — collapsible */}
                            <Box
                                onClick={() => setDepsExpanded(v => !v)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    cursor: 'pointer', mt: 1.5, mb: 0.5, px: 0.5, py: 0.25, borderRadius: 1,
                                    '&:hover': { bgcolor: '#f0f4ff' },
                                }}
                            >
                                <Box display="flex" alignItems="center" gap={0.5}>
                                    <Typography variant="caption" fontWeight={700} color="#0f2744"
                                        sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                        Dependencies
                                    </Typography>
                                    {(selectedOperation.dependencies || []).length > 0 && (
                                        <Chip size="small" label={(selectedOperation.dependencies || []).length}
                                            sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#1677ff', color: '#fff' }} />
                                    )}
                                </Box>
                                {depsExpanded
                                    ? <ExpandLess sx={{ fontSize: 16, color: '#6b7280' }} />
                                    : <ExpandMore sx={{ fontSize: 16, color: '#6b7280' }} />
                                }
                            </Box>

                            {selfDepAlert && (
                                <Alert severity="warning" sx={{ mb: 0.75, py: 0.25, fontSize: '0.74rem' }}
                                    onClose={() => setSelfDepAlert(false)}>
                                    An operation cannot depend on itself.
                                </Alert>
                            )}

                            <Collapse in={depsExpanded}>
                                {(operations || []).filter(op =>
                                    (op.id || op._tempId) !== (selectedOperation.id || selectedOperation._tempId)
                                ).length === 0 ? (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, pl: 0.5 }}>
                                        Add more operations to set dependencies.
                                    </Typography>
                                ) : (
                                    <Box sx={{ mb: 1.5 }}>
                                        {(operations || []).filter(op =>
                                            (op.id || op._tempId) !== (selectedOperation.id || selectedOperation._tempId)
                                        ).map(depOp => {
                                            const depOpId = depOp.id || depOp._tempId;
                                            const existingDep = (selectedOperation.dependencies || []).find(
                                                d => d.dependsOnRoutingOperationId === depOpId
                                            );
                                            const isSelected = Boolean(existingDep);

                                            const toggleDep = () => {
                                                setSelfDepAlert(false);
                                                const currentDeps = selectedOperation.dependencies || [];
                                                const nextDeps = isSelected
                                                    ? currentDeps.filter(d => d.dependsOnRoutingOperationId !== depOpId)
                                                    : [...currentDeps, { dependsOnRoutingOperationId: depOpId, dependsOnSequenceNumber: depOp.sequenceNumber, dependencyType: 'SEQUENTIAL', isRequired: true }];
                                                setSelectedOperation({ ...selectedOperation, dependencies: nextDeps });
                                            };

                                            const setDepType = (type) => {
                                                const currentDeps = selectedOperation.dependencies || [];
                                                const nextDeps = currentDeps.map(d =>
                                                    d.dependsOnRoutingOperationId === depOpId ? { ...d, dependencyType: type } : d
                                                );
                                                setSelectedOperation({ ...selectedOperation, dependencies: nextDeps });
                                            };

                                            return (
                                                <Box
                                                    key={depOpId}
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 0.75,
                                                        p: 0.75, mb: 0.5, borderRadius: 1,
                                                        border: isSelected ? '1px solid #1677ff' : '1px solid #e5e7eb',
                                                        bgcolor: isSelected ? '#f0f7ff' : 'transparent',
                                                        cursor: 'pointer', transition: 'all 0.1s',
                                                        '&:hover': { bgcolor: isSelected ? '#e6f0ff' : '#f9fafb' },
                                                    }}
                                                    onClick={toggleDep}
                                                >
                                                    <Checkbox
                                                        size="small"
                                                        checked={isSelected}
                                                        onChange={toggleDep}
                                                        onClick={e => e.stopPropagation()}
                                                        sx={{ p: 0.25, '&.Mui-checked': { color: '#1677ff' } }}
                                                    />
                                                    <Typography sx={{ fontSize: '0.78rem', fontWeight: 500, flex: 1, color: '#0f2744' }}>
                                                        {depOp.sequenceNumber}. {depOp.name || 'Unnamed'}
                                                    </Typography>
                                                    {isSelected && (
                                                        <TextField
                                                            select
                                                            size="small"
                                                            value={existingDep?.dependencyType || 'SEQUENTIAL'}
                                                            onClick={e => e.stopPropagation()}
                                                            onChange={e => { e.stopPropagation(); setDepType(e.target.value); }}
                                                            sx={{
                                                                minWidth: 140,
                                                                '& .MuiInputBase-root': { height: 26, fontSize: '0.72rem' },
                                                                '& .MuiSelect-select': { py: 0.25, pr: 2 },
                                                            }}
                                                        >
                                                            <MenuItem value="SEQUENTIAL">
                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                    <span style={{ fontSize: 10 }}>&#9654;</span> Sequential
                                                                </Box>
                                                            </MenuItem>
                                                            <MenuItem value="PARALLEL_ALLOWED">
                                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                                    <span style={{ fontSize: 10, color: '#1677ff' }}>&#8649;</span> Parallel Allowed
                                                                </Box>
                                                            </MenuItem>
                                                        </TextField>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Collapse>
                            <TextField fullWidth size="small" label="Internal Notes" sx={{ ...fieldSx, mb: 2 }}
                                value={selectedOperation.notes || ""}
                                onChange={(e) => setSelectedOperation({ ...selectedOperation, notes: e.target.value })}
                                multiline minRows={2}
                                placeholder="Internal remarks (not shown in Work Order)..."
                            />

                            {/* ── Attachments Section ── */}
                            {selectedOperation.id && (
                                <>
                                    <Box
                                        onClick={() => setAttachmentsExpanded(v => !v)}
                                        sx={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer', mb: 0.5, px: 0.5, py: 0.25, borderRadius: 1,
                                            '&:hover': { bgcolor: '#f0f4ff' },
                                        }}
                                    >
                                        <Box display="flex" alignItems="center" gap={0.5}>
                                            <AttachFile sx={{ fontSize: 14, color: '#0f2744' }} />
                                            <Typography variant="caption" fontWeight={700} color="#0f2744"
                                                sx={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                                Attachments
                                            </Typography>
                                            {opAttachments.length > 0 && (
                                                <Chip size="small" label={opAttachments.length}
                                                    sx={{ height: 16, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#1677ff', color: '#fff' }} />
                                            )}
                                        </Box>
                                        {attachmentsExpanded
                                            ? <ExpandLess sx={{ fontSize: 16, color: '#6b7280' }} />
                                            : <ExpandMore sx={{ fontSize: 16, color: '#6b7280' }} />
                                        }
                                    </Box>

                                    <Collapse in={attachmentsExpanded}>
                                        <Box sx={{ mb: 1.5 }}>
                                            <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                                                <Button
                                                    component="label"
                                                    variant="outlined"
                                                    size="small"
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', borderColor: BORDER_COLOR, color: '#374151' }}
                                                >
                                                    Choose File
                                                    <input type="file" hidden
                                                        onChange={(e) => setAttachmentFile(e.target.files?.[0] || null)} />
                                                </Button>
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {attachmentFile ? attachmentFile.name : 'No file selected'}
                                                </Typography>
                                                <Button
                                                    onClick={handleAttachmentUpload}
                                                    variant="contained"
                                                    size="small"
                                                    disabled={!attachmentFile || attachmentUploading}
                                                    sx={{ textTransform: 'none', fontSize: '0.75rem', bgcolor: '#1565c0', '&:hover': { bgcolor: '#0d47a1' }, minWidth: 70 }}
                                                >
                                                    {attachmentUploading ? <CircularProgress size={14} color="inherit" /> : 'Upload'}
                                                </Button>
                                            </Box>

                                            {opAttachments.length === 0 && (
                                                <Box sx={{ py: 2, textAlign: 'center', bgcolor: '#fafbfc', borderRadius: 1, border: `1px dashed ${BORDER_COLOR}` }}>
                                                    <Typography variant="caption" color="text.secondary">No attachments</Typography>
                                                </Box>
                                            )}

                                            <List dense disablePadding>
                                                {opAttachments.map((file) => (
                                                    <ListItem
                                                        key={file.id}
                                                        sx={{
                                                            borderRadius: 1, border: `1px solid ${BORDER_COLOR}`, mb: 0.5, bgcolor: '#fafbfc',
                                                            '&:hover': { bgcolor: '#f0f4f8' }, py: 0.25, px: 1,
                                                        }}
                                                        secondaryAction={
                                                            <IconButton edge="end" size="small" onClick={() => handleAttachmentDelete(file.id)}>
                                                                <DeleteOutline sx={{ fontSize: 16, color: '#d32f2f' }} />
                                                            </IconButton>
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={
                                                                <Typography variant="body2" fontWeight={500}
                                                                    sx={{ color: '#1565c0', cursor: 'pointer', fontSize: '0.78rem' }}
                                                                    onClick={() => handleAttachmentDownload(file.id, file.fileName)}>
                                                                    {file.originalName || file.fileName?.replace(/^\d+_/, '')}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                                                    {file.uploadedAt ? new Date(file.uploadedAt).toLocaleDateString('en-GB') : ''}
                                                                </Typography>
                                                            }
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                        </Box>
                                    </Collapse>
                                </>
                            )}

                            {!selectedOperation.id && (
                                <Alert severity="info" sx={{ mb: 1.5, py: 0.25, fontSize: '0.74rem' }}>
                                    Save the operation first to attach files.
                                </Alert>
                            )}

                            {/* Action Buttons */}
                            <Typography sx={{ fontSize: '0.72rem', color: '#6b7280', mt: 1 }}>
                                Changes are kept as you edit, across operations and tabs. Save the BOM to store them.
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Button variant="outlined" color="error" fullWidth size="small"
                                    sx={{ textTransform: 'none', fontWeight: 500, borderRadius: 1.5 }}
                                    onClick={handleDeleteOperation}>
                                    Delete
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Grid>
        </Grid>
    )
}

function ArrowDown() {
    return (
        <svg width="20" height="20" viewBox="0 0 24 24">
            <path d="M12 2 L12 18 M6 12 L12 18 L18 12"
                stroke="#9ca3af" strokeWidth="2" fill="none" strokeLinecap="round" />
        </svg>
    );
}
